/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './ManageMemoryEditsSubscreen.messages';
import type { MemoryEditItem } from './types';

const styles = cssMap({
	deleteConfirmRow: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		gap: token('space.150'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	deleteConfirmRowFirst: {
		backgroundColor: token('elevation.surface.sunken'),
		borderTopRightRadius: token('radius.xlarge'),
		borderTopLeftRadius: token('radius.xlarge'),
	},
	memoryEditPreview: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		boxShadow: token('elevation.shadow.overflow'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	deleteConfirmContent: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.150'),
		flexWrap: 'nowrap',
	},
	deleteConfirmTextWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		minWidth: '0px',
		flex: '1 1 auto',
		paddingInlineStart: token('space.100'),
	},
	deleteConfirmActions: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.100'),
		flexShrink: 0,
	},
});

type EditDeleteConfirmProps = {
	edit: MemoryEditItem;
	onCancel: () => void;
	onRemove: () => void;
	isFirst: boolean;
	isLoading?: boolean;
};

export const EditDeleteConfirm = ({
	edit,
	onCancel,
	onRemove,
	isFirst,
	isLoading = false,
}: EditDeleteConfirmProps): JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={cx(styles.deleteConfirmRow, isFirst && styles.deleteConfirmRowFirst)}>
			<Box xcss={styles.memoryEditPreview}>
				<Text>{edit.content}</Text>
			</Box>
			<Box xcss={styles.deleteConfirmContent}>
				<Box xcss={styles.deleteConfirmTextWrapper}>
					<Text weight="bold">{formatMessage(messages.deleteConfirmHeading)}</Text>
					<Text size="small" color="color.text.subtlest">
						{formatMessage(messages.deleteConfirmDescription)}
					</Text>
				</Box>
				<Box xcss={styles.deleteConfirmActions}>
					<Button appearance="subtle" onClick={onCancel} isDisabled={isLoading}>
						{formatMessage(messages.deleteConfirmCancel)}
					</Button>
					<Button
						appearance="warning"
						onClick={onRemove}
						isLoading={isLoading}
						isDisabled={isLoading}
					>
						{formatMessage(messages.deleteConfirmRemove)}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
